/**
 * VireoHR - Employee Schedule Screen
 * Ultra-Minimal Design
 */

import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';

import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';
import { Schedule } from '@/types/models';

export default function EmployeeSchedule() {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchSchedules();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Fixed: Added user?.id to dependency array

  const fetchSchedules = async () => {
    if (!user?.id) return;

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const schedulesRef = collection(firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('employeeIds', 'array-contains', user.id),
        where('startTimestamp', '>=', today.toISOString()),
        orderBy('startTimestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Schedule[];

      setSchedules(data);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Layout title="Schedule">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Schedule">
      <div className="p-4">
        <h1 className="text-base font-semibold text-[#0D9488] mb-4">My Shifts</h1>

        {schedules.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <p className="text-sm text-[#D4AF37]">No upcoming shifts</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-3">
            {schedules.map((schedule) => (
              <Card key={schedule.id}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#0D9488]">{formatDate(schedule.startTimestamp)}</p>
                    <p className="text-xs text-[#D4AF37] mt-1">
                      {formatTime(schedule.startTimestamp)} - {formatTime(schedule.endTimestamp)}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-[#D4AF37] bg-opacity-20 text-[#0D9488] rounded border border-[#D4AF37]">
                    Scheduled
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
